import { useCallback, useEffect, useRef } from "react";

import {
    MiddleTierToolResponse,
    ResponseAudioTranscriptDelta,
    ResponseDone,
    ResponseInputAudioTranscriptionCompleted
} from "@/types";

const log = (...args: unknown[]) => console.log("[WebRTC]", ...args);
const logErr = (...args: unknown[]) => console.error("[WebRTC]", ...args);

const IDLE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

// ---- Runtime type guards for incoming WebSocket messages ---------------------
// These check the minimal shape we depend on; they intentionally do not validate
// every nested field (the source is our own backend, not an untrusted peer).
function isResponseDone(msg: Record<string, unknown>): msg is ResponseDone {
    return msg.type === "response.done" || msg.type === "response.output_audio_transcript.done";
}
function isResponseAudioTranscriptDelta(
    msg: Record<string, unknown>
): msg is ResponseAudioTranscriptDelta {
    return msg.type === "response.output_audio_transcript.delta" && typeof msg.delta === "string";
}
function isMiddleTierToolResponse(
    msg: Record<string, unknown>
): msg is MiddleTierToolResponse {
    return (
        msg.type === "extension.middle_tier_tool_response" &&
        typeof (msg as { tool_name?: unknown }).tool_name === "string"
    );
}
function isInputAudioTranscriptionCompleted(
    msg: Record<string, unknown>
): msg is ResponseInputAudioTranscriptionCompleted {
    return (
        msg.type === "conversation.item.input_audio_transcription.completed" &&
        typeof (msg as { transcript?: unknown }).transcript === "string"
    );
}

type Parameters = {
    wsPath?: string;
    deployment?: string;
    voice?: string;
    enableInputAudioTranscription?: boolean;
    onSessionStarted?: () => void;
    onSessionEnded?: () => void;
    onError?: (event: Event | Error) => void;
    onReceivedResponseAudioTranscriptDelta?: (message: ResponseAudioTranscriptDelta) => void;
    onReceivedInputAudioBufferSpeechStarted?: () => void;
    onReceivedResponseDone?: (message: ResponseDone) => void;
    onReceivedInputAudioTranscriptionCompleted?: (message: ResponseInputAudioTranscriptionCompleted) => void;
    onReceivedExtensionToolResponse?: (message: MiddleTierToolResponse) => void;
    onReceivedOutputAudioBufferStopped?: () => void;
    onAudioLevel?: (level: number) => void;
};

export default function useWebRTC(params: Parameters): {
    startSession: () => Promise<void>;
    stopSession: () => void;
    pauseSession: () => void;
    resumeSession: () => void;
} {
    const pcRef = useRef<RTCPeerConnection | null>(null);
    const dcRef = useRef<RTCDataChannel | null>(null);
    const audioElRef = useRef<HTMLAudioElement | null>(null);
    const backendWsRef = useRef<WebSocket | null>(null);
    const levelTimerRef = useRef<number | null>(null);
    const audioCtxRef = useRef<AudioContext | null>(null);
    const micTracksRef = useRef<MediaStreamTrack[]>([]);
    const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const paramsRef = useRef(params);
    paramsRef.current = params;

    const stopSession = useCallback(() => {
        log("stopSession called");
        if (idleTimerRef.current !== null) {
            clearTimeout(idleTimerRef.current);
            idleTimerRef.current = null;
        }
        micTracksRef.current = [];
        if (levelTimerRef.current !== null) {
            cancelAnimationFrame(levelTimerRef.current);
            levelTimerRef.current = null;
        }
        audioCtxRef.current?.close();
        audioCtxRef.current = null;
        dcRef.current?.close();
        dcRef.current = null;
        pcRef.current?.getSenders().forEach(s => s.track?.stop());
        pcRef.current?.close();
        pcRef.current = null;
        if (audioElRef.current) {
            audioElRef.current.pause();
            audioElRef.current.srcObject = null;
        }
        backendWsRef.current?.close();
        backendWsRef.current = null;
        paramsRef.current.onSessionEnded?.();
    }, []);

    const resetIdleTimer = useCallback(() => {
        if (idleTimerRef.current !== null) clearTimeout(idleTimerRef.current);
        idleTimerRef.current = setTimeout(() => {
            log("Idle timeout (10 min) — stopping session");
            stopSession();
        }, IDLE_TIMEOUT_MS);
    }, [stopSession]);

    const pauseSession = useCallback(() => {
        log("pauseSession — muting mic, pausing audio");
        micTracksRef.current.forEach(t => { t.enabled = false; });
        audioElRef.current?.pause();
        backendWsRef.current?.send(JSON.stringify({ type: "session.pause" }));
    }, []);

    const resumeSession = useCallback(() => {
        log("resumeSession — unmuting mic, resuming audio");
        micTracksRef.current.forEach(t => { t.enabled = true; });
        audioElRef.current?.play().catch(() => {});
        backendWsRef.current?.send(JSON.stringify({ type: "session.resume" }));
        resetIdleTimer();
    }, [resetIdleTimer]);

    const startSession = useCallback(async () => {
        const p = paramsRef.current;
        const wsPath = p.wsPath ?? "/ws";
        log("startSession — deployment:", p.deployment, "voice:", p.voice, "wsPath:", wsPath);

        // 1. Create peer connection
        const pc = new RTCPeerConnection();
        pcRef.current = pc;
        pc.oniceconnectionstatechange = () => log("ICE connection state:", pc.iceConnectionState);
        pc.onconnectionstatechange = () => log("Peer connection state:", pc.connectionState);
        pc.onicegatheringstatechange = () => log("ICE gathering state:", pc.iceGatheringState);
        pc.onsignalingstatechange = () => log("Signaling state:", pc.signalingState);

        // 1b. Create audio context early for both mic and remote audio analysers
        log("Creating audio context for analysis...");
        const audioCtx = new AudioContext();
        audioCtxRef.current = audioCtx;

        // 2. Set up remote audio playback element
        const audioEl = document.createElement("audio");
        audioEl.autoplay = true;
        audioElRef.current = audioEl;
        
        pc.ontrack = e => {
            log("Remote track received, kind:", e.track.kind, "streams:", e.streams.length);
            audioEl.srcObject = e.streams[0];
            audioEl.play().catch(err => log("autoplay blocked, will retry on user gesture:", err));
        };

        // 3. Capture microphone and set up audio level analysis
        log("Requesting microphone access...");
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        log("Microphone access granted, tracks:", stream.getTracks().map(t => t.label));
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        
        const buf = new Uint8Array(analyser.frequencyBinCount);
        
        const measureLevel = () => {
            analyser.getByteFrequencyData(buf);
            const avg = buf.reduce((a, b) => a + b, 0) / buf.length;
            const level = avg / 255;
            paramsRef.current.onAudioLevel?.(level);
            levelTimerRef.current = requestAnimationFrame(measureLevel);
        };
        levelTimerRef.current = requestAnimationFrame(measureLevel);

        // 4. Add mic track to peer connection and save refs for pause/resume
        stream.getTracks().forEach(t => pc.addTrack(t, stream));
        micTracksRef.current = stream.getTracks();

        // 5. Create data channel BEFORE creating offer
        const dc = pc.createDataChannel("realtime-channel");
        dcRef.current = dc;
        log("Data channel created, label:", dc.label);

        dc.onopen = () => {
            log("Data channel open, readyState:", dc.readyState);
            // Session is already fully configured via the ephemeral token.
            // The GA WebRTC protocol does not support session.update from the client
            // for Azure AI Foundry models (input_audio_transcription is not supported).
        };
        dc.onclose = () => log("Data channel closed");
        dc.onerror = e => logErr("Data channel error:", e);

        dc.onmessage = e => {
            let msg: Record<string, unknown>;
            try {
                msg = JSON.parse(e.data as string);
            } catch {
                return;
            }
            log("DC message:", msg.type, msg.type === "error" ? msg : "");
            const cp = paramsRef.current;
            switch (msg.type) {
                case "response.output_audio_transcript.delta":
                    if (isResponseAudioTranscriptDelta(msg)) {
                        cp.onReceivedResponseAudioTranscriptDelta?.(msg);
                    }
                    break;
                case "response.output_audio_transcript.done":
                    log("Audio transcript done");
                    if (isResponseDone(msg)) {
                        cp.onReceivedResponseDone?.(msg);
                    }
                    break;
                case "input_audio_buffer.speech_started":
                    // Pause remote audio so the user's speech interrupts the model
                    log("Speech started — pausing remote audio");
                    audioElRef.current?.pause();
                    resetIdleTimer();
                    cp.onReceivedInputAudioBufferSpeechStarted?.();
                    break;
                case "output_audio_buffer.started":
                    // Model is sending audio — ensure playback is running
                    log("Output audio buffer started — resuming audio");
                    audioElRef.current?.play().catch(() => {});
                    break;
                case "response.done":
                    // Resume remote audio to be ready for next response
                    log("Response done");
                    audioElRef.current?.play().catch(() => {});
                    resetIdleTimer();
                    if (isResponseDone(msg)) {
                        cp.onReceivedResponseDone?.(msg);
                    }
                    break;
                case "error":
                    logErr("Azure error via data channel:", JSON.stringify(msg));
                    break;
            }
        };

        // 6. Create SDP offer
        log("Creating SDP offer...");
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        log("Local description set, SDP length:", offer.sdp?.length);

        // 7. Open backend WebSocket for signalling
        const proto = location.protocol === "https:" ? "wss:" : "ws:";
        const wsUrl = `${proto}//${location.host}${wsPath}`;
        log("Connecting to backend WS:", wsUrl);
        const ws = new WebSocket(wsUrl);
        backendWsRef.current = ws;

        await new Promise<void>((resolve, reject) => {
            ws.onopen = () => {
                log("Backend WS open, sending session.create");
                ws.send(
                    JSON.stringify({
                        type: "session.create",
                        sdp: offer.sdp!,
                        deployment: paramsRef.current.deployment,
                        voice: paramsRef.current.voice
                    })
                );
                resolve();
            };
            ws.onerror = () => reject(new Error("Backend WS connection error"));
        });

        // 8. Wait for session.created carrying the SDP answer
        log("Waiting for session.created from backend...");
        await new Promise<void>((resolve, reject) => {
            const handler = (e: MessageEvent) => {
                let msg: Record<string, unknown>;
                try {
                    msg = JSON.parse(e.data as string);
                } catch {
                    return;
                }
                log("Backend WS message during setup:", msg.type);
                if (msg.type === "session.created") {
                    log("Got session.created, setting remote description, SDP length:", (msg.sdp as string)?.length);
                    pc.setRemoteDescription({ type: "answer", sdp: msg.sdp as string })
                        .then(() => {
                            log("Remote description set — WebRTC handshake complete");
                            resetIdleTimer();
                            paramsRef.current.onSessionStarted?.();
                            resolve();
                        })
                        .catch(err => { logErr("setRemoteDescription failed:", err); reject(err); });
                    ws.removeEventListener("message", handler);
                } else if (msg.type === "error") {
                    logErr("Backend WS setup error:", msg.error);
                    reject(new Error(String(msg.error)));
                    ws.removeEventListener("message", handler);
                }
            };
            ws.addEventListener("message", handler);
            ws.onerror = () => reject(new Error("Backend WS error during session creation"));
        });

        // 9. Handle ongoing backend WS messages (tool responses, transcriptions, audio events)
        ws.onmessage = e => {
            let msg: Record<string, unknown>;
            try {
                msg = JSON.parse(e.data as string);
            } catch {
                return;
            }
            log("Backend WS message (ongoing):", msg.type);
            const cp = paramsRef.current;
            switch (msg.type) {
                case "response.done":
                    if (isResponseDone(msg)) {
                        cp.onReceivedResponseDone?.(msg);
                    }
                    break;
                case "extension.middle_tier_tool_response":
                    if (isMiddleTierToolResponse(msg)) {
                        cp.onReceivedExtensionToolResponse?.(msg);
                    }
                    break;
                case "conversation.item.input_audio_transcription.completed":
                    if (isInputAudioTranscriptionCompleted(msg)) {
                        cp.onReceivedInputAudioTranscriptionCompleted?.(msg);
                    }
                    break;
                case "output_audio_buffer.stopped":
                    cp.onReceivedOutputAudioBufferStopped?.();
                    break;
            }
        };

        ws.onclose = e => { log("Backend WS closed, code:", e.code, "reason:", e.reason); stopSession(); };
        ws.onerror = e => logErr("Backend WS error (ongoing):", e);
    }, [stopSession, resetIdleTimer]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopSession();
        };
    }, [stopSession]);

    return { startSession, stopSession, pauseSession, resumeSession };
}
